import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AuthLayout, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, LoadingState, Textarea, useToast } from '@buzzbee/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { del, errorMessage, formatWhen, get, post, put } from '../lib/api';
import { useRouter } from '../lib/router';
import { useBrand, useWorkspace } from '../lib/session';
import { Logo } from './Logo';

/** "$25" or "$25.50". */
export const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

const DEPOSIT_LABEL: Record<string, { label: string; variant: 'success' | 'warning' | 'default' | 'info' }> = {
  requested: { label: 'Deposit pending', variant: 'warning' },
  paid: { label: 'Deposit paid', variant: 'success' },
  expired: { label: 'Deposit not paid', variant: 'default' },
  refunded: { label: 'Deposit refunded', variant: 'info' },
};

/** Deposit amount and state on a booking (nothing when it has no deposit). */
export function DepositBadge({ cents, status }: { cents: number | null | undefined; status: string | null | undefined }) {
  if (!status || !cents) return null;
  const d = DEPOSIT_LABEL[status] ?? { label: status, variant: 'default' as const };
  return (
    <Badge variant={d.variant}>
      {d.label}: {dollars(cents)}
    </Badge>
  );
}

function useSave(fn: (v: any) => Promise<unknown>, label = 'Saved') {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({ mutationFn: fn, onSuccess: (r: any) => { success(label, r?.message); void qc.invalidateQueries(); }, onError: (e) => error('Could not save', errorMessage(e)) });
}

function Panel({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

/** Front desk: "time for your next visit" texts (ReceptionistBee review_requests). */
export function RebookingSettings() {
  const ws = useWorkspace();
  const q = useQuery({ queryKey: ['rebooking'], queryFn: () => get('/api/reviews/rebooking') });
  const [f, setF] = useState({ enabled: false, afterDays: 28, message: '' });
  useEffect(() => {
    if (q.data) setF({ enabled: q.data.enabled, afterDays: q.data.afterDays, message: q.data.message ?? '' });
  }, [q.data]);
  const save = useSave(() => put('/api/reviews/rebooking', { enabled: f.enabled, afterDays: Number(f.afterDays), message: f.message.trim() || null }));
  if (!q.data) return <LoadingState />;
  const custom = (q.data.services as { name: string; rebookAfterDays: number | null }[]).filter((s) => s.rebookAfterDays);
  return (
    <Panel title="Rebooking reminders" description="A friendly “time for your next visit” text with your booking link, once per visit, when a customer has not booked again.">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.enabled} disabled={ws.role === 'staff'} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Send rebooking reminders
      </label>
      <p className="text-sm text-muted-foreground">{q.data.policy} At most {q.data.throttlePerDay} a day.</p>
      <div className="space-y-1.5">
        <Label htmlFor="rb-d">Suggest the next visit after (days)</Label>
        <Input id="rb-d" type="number" min={1} max={365} className="w-28" value={f.afterDays} onChange={(e) => setF({ ...f, afterDays: Number(e.target.value) })} />
        <p className="text-xs text-muted-foreground">
          {custom.length ? `Your own interval for ${custom.map((s) => `${s.name} (${s.rebookAfterDays} days)`).join(', ')}. ` : ''}Set a different interval per service in Settings, Services.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rb-m">Custom message (optional)</Label>
        <Textarea id="rb-m" rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder={`${ws.name}: it's been a while since your {service} with {staff}. Book your next one: {link}`} />
        <p className="text-xs text-muted-foreground">
          You can use {'{name}'}, {'{service}'}, {'{staff}'}, {'{business}'} and {'{link}'}{q.data.bookingLink ? ` (${q.data.bookingLink})` : ''}. “Reply STOP to opt out” is added automatically.
        </p>
      </div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending} disabled={ws.role === 'staff'}>
        Save
      </Button>
      <p className="text-sm text-muted-foreground">{q.data.log.filter((n: any) => n.status === 'sent').length} rebooking texts sent recently.</p>
    </Panel>
  );
}

/** Front desk: deposits on the business's own Stripe account (ReceptionistBee no_show_rescue). */
export function DepositSettings() {
  const ws = useWorkspace();
  const q = useQuery({ queryKey: ['deposits'], queryFn: () => get('/api/no-show/deposits') });
  const [f, setF] = useState({ enabled: false, holdMinutes: 60 });
  const [key, setKey] = useState('');
  useEffect(() => {
    if (q.data) setF({ enabled: q.data.enabled, holdMinutes: q.data.holdMinutes });
  }, [q.data]);
  const save = useSave(() => put('/api/no-show/deposits', { enabled: f.enabled, holdMinutes: Number(f.holdMinutes) }));
  const connect = useSave(() => put('/api/no-show/deposits/stripe', { secretKey: key.trim() }).then((r) => { setKey(''); return r; }), 'Stripe connected');
  const test = useMutation({ mutationFn: () => post<{ ok: boolean; message: string }>('/api/no-show/deposits/stripe/test') });
  const disconnect = useSave(() => del('/api/no-show/deposits/stripe'), 'Stripe disconnected');
  if (!q.data) return <LoadingState />;
  const admin = ws.role !== 'staff';
  const withDeposit = (q.data.services as { name: string; depositCents: number }[]).filter((s) => s.depositCents > 0);
  return (
    <div className="space-y-6">
      <Panel title="Deposits" description="Protect your time from no-shows: services with a deposit are held while the customer pays by a texted link, and confirmed once paid. Unpaid holds are released automatically.">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.enabled} disabled={!admin} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Ask for deposits
        </label>
        <div className="space-y-1.5">
          <Label htmlFor="dp-h">Hold the time for (minutes, at least 30)</Label>
          <Input id="dp-h" type="number" min={30} max={1440} className="w-28" value={f.holdMinutes} onChange={(e) => setF({ ...f, holdMinutes: Number(e.target.value) })} />
        </div>
        <p className="text-sm text-muted-foreground">
          {withDeposit.length ? `Deposits in ${q.data.currency.toUpperCase()}: ${withDeposit.map((s) => `${s.name} ${dollars(s.depositCents)}`).join(', ')}.` : 'No service has a deposit yet.'} Set the amount per service in Settings, Services.
        </p>
        {f.enabled && !q.data.account ? <Alert variant="warning" title="Connect Stripe to start">Bookings go through without a deposit until your Stripe account is connected.</Alert> : null}
        <Button onClick={() => save.mutate(null)} loading={save.isPending} disabled={!admin}>
          Save
        </Button>
      </Panel>
      <Panel title="Your Stripe account" description="Deposits are paid straight into your own Stripe account. Create a restricted key (Developers, API keys) with Checkout Sessions write and Account read access, and paste it here. It is stored encrypted and never shown again.">
        {q.data.account ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            Connected to <strong>{q.data.account.accountName ?? 'Stripe'}</strong>
            <Badge variant={q.data.account.livemode ? 'success' : 'warning'}>{q.data.account.livemode ? 'live' : 'test mode'}</Badge>
            <span className="text-muted-foreground">key ending in <span className="figures">{q.data.account.keyHint}</span></span>
            <Button size="sm" variant="outline" onClick={() => test.mutate()} loading={test.isPending} disabled={!admin}>
              Test
            </Button>
            <Button size="sm" variant="ghost" onClick={() => disconnect.mutate(null)} loading={disconnect.isPending} disabled={!admin}>
              Disconnect
            </Button>
          </div>
        ) : null}
        {test.data ? <Alert variant={test.data.ok ? 'success' : 'destructive'}>{test.data.message}</Alert> : null}
        {test.error ? <Alert variant="destructive">{errorMessage(test.error)}</Alert> : null}
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="dp-key">{q.data.account ? 'Replace the key' : 'Stripe secret or restricted key'}</Label>
            <Input id="dp-key" type="password" autoComplete="off" className="w-80" placeholder="rk_live_…" value={key} onChange={(e) => setKey(e.target.value)} disabled={!admin} />
          </div>
          <Button onClick={() => connect.mutate(null)} loading={connect.isPending} disabled={!admin || key.trim().length < 10}>
            {q.data.account ? 'Replace' : 'Connect Stripe'}
          </Button>
        </div>
      </Panel>
      <Panel title="Recent deposits">
        {q.data.recent.length === 0 ? <p className="text-sm text-muted-foreground">No deposits yet.</p> : null}
        <div className="divide-y text-sm">
          {q.data.recent.map((a: any) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="figures w-40">{formatWhen(a.startsAt, ws.timezone)}</span>
              <span className="flex-1">
                {a.serviceName} · {a.contactName ?? 'Customer'}
              </span>
              <DepositBadge cents={a.depositCents} status={a.depositStatus} />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/** Public page Stripe Checkout returns the customer to after (not) paying a deposit. */
export function DepositResultPage() {
  useBrand(null);
  const { search } = useRouter();
  const paid = search.get('status') === 'paid';
  return (
    <AuthLayout logo={<Logo />} title={paid ? 'Thanks, your deposit is paid' : 'Deposit not paid'}>
      {paid ? (
        <Alert variant="success">Your appointment is being confirmed; a text with the details is on its way. You can close this page.</Alert>
      ) : (
        <Alert variant="warning">Your time is still held until the hold runs out. Open the link in your text again to pay the deposit, or call the business.</Alert>
      )}
    </AuthLayout>
  );
}
